import React, { useContext, memo } from "react";
import { Elements } from "@stripe/react-stripe-js";
import Layout from "layout/Layout";
import useAppConfig from "hooks/useAppConfig";
import CircularProgress from '@mui/material/CircularProgress';
import { LoaderBox, TCard, TWrapper } from "components/styles";
import { TipInfoSettingsProvider } from "context/TipInfoSettingsContext";
import { PAYMENT_SYSTEM_TYPES, PaymentSystemContext } from "context/PaymentSystemContext";
import ForwardPaymentProvider from "context/TPaymentContext";
import TOPaid from "components/Payments/TOPaid";
import ForwardPayments from "components/Payments/ForwardPayments";
import StripePayments from "components/Payments/StripePayments";
import AdyenPayments from "components/Payments/AdyenPayments";
import TOCardHeader from "components/Header/TOCardHeader";

// Loading fallback component
const LoadingFallback = () => (
  <Layout>
    <TWrapper>
      <TCard>
        <LoaderBox>
          <CircularProgress />
        </LoaderBox>
      </TCard>
    </TWrapper>
  </Layout>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Layout>
          <TWrapper>
            <TCard>
              <LoaderBox>
                <p>Something went wrong. Please refresh the page.</p>
              </LoaderBox>
            </TCard>
          </TWrapper>
        </Layout>
      );
    }

    return this.props.children;
  }
}

// Optimized Context Providers wrapper
const AppProviders = memo(({ children, paymentSystem, stripeInstance }) => (
  <TipInfoSettingsProvider>
    <ForwardPaymentProvider>
      {paymentSystem === PAYMENT_SYSTEM_TYPES.STRIPE && stripeInstance ? (
        <Elements stripe={stripeInstance}>
          {children}
        </Elements>
      ) : (
        children
      )}
    </ForwardPaymentProvider>
  </TipInfoSettingsProvider>
));

// Main App Component
const App = memo(() => {
  const { 
    loaded, 
    error, 
    isWebView, 
    bId, 
    oId, 
    isPaid, 
    loading,
    stripeInstance
  } = useAppConfig();
  
  const { paymentSystem } = useContext(PaymentSystemContext);
  
  // Show loading state
  if (loading || !paymentSystem) {
    return <LoadingFallback />;
  }

  // Main content
  const renderContent = () => {
    if (isPaid) {
      return (
        <TOPaid isWebView={isWebView} oId={oId} />
      );
    }

    return (
      <>
        {!isWebView && (
          <TOCardHeader />
        )}
        {loaded && !error && (
          <AppProviders paymentSystem={paymentSystem} stripeInstance={stripeInstance}>
            {paymentSystem === PAYMENT_SYSTEM_TYPES.FORWARD ? (
              <ForwardPayments 
                isWebView={isWebView}
                loaded={loaded} 
                error={error} 
                bId={bId} 
                oId={oId} 
              />
            ) : paymentSystem === PAYMENT_SYSTEM_TYPES.ADYEN ? (
              <AdyenPayments
                isWebView={isWebView}
                loaded={loaded}
                error={error}
                bId={bId}
                oId={oId}
              />
            ) : (
              <StripePayments 
                isWebView={isWebView}
                loaded={loaded} 
                error={error} 
                bId={bId} 
                oId={oId} 
              />
            )}
          </AppProviders>
        )}
      </>
    );
  };

  return (
    <ErrorBoundary>
      <Layout>
        <TWrapper>
          <TCard>
            {renderContent()}
          </TCard>
        </TWrapper>
      </Layout>
    </ErrorBoundary>
  );
});

App.displayName = 'App';

export default App;
